import { InfoCircleFilled } from '@ant-design/icons'
import { Popover, Tooltip } from 'antd'
import { TableColumnsType } from 'antd/lib'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'
import { useMemo } from 'react'

import { DangerIcon, WarningIcon } from 'src/assets'
import { Text } from 'src/components'
import { getCostPerUnitTva, getTotalTvaPrice } from 'src/features/stock/utils'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'

import { QuantityPopover } from '../components'

export const useColumns = () => {
  const { t, currentLang } = useLocales()

  const palette = useTheme()

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'internationalName',
        title: t('common.international_name'),
        render: ({ internationalName, doses }) => (
          <Text weight={400} color={palette.blue6}>
            {internationalName?.name} {doses}
          </Text>
        )
      },
      {
        key: 'id',
        title: t('stock_applications.series'),
        dataIndex: 'barCode'
      },
      {
        key: 'producer',
        title: t('common.producer'),
        render: ({ producer }) => (
          <Text color={palette.cyan7}>{producer?.name}</Text>
        )
      },
      {
        key: 'measurementUnit',
        title: t('medicine_register.consumables.measurement_unit'),
        align: 'center',
        render: ({ measurementUnit }) => (
          <Text weight={400} color={palette.blue}>
            {measurementUnit?.name
              ? measurementUnit?.name?.[currentLang?.value]
              : '-'}
          </Text>
        )
      },
      {
        key: 'productGroup',
        title: t('common.group'),
        render: ({ productGroup }) => (
          <Text weight={400} color={palette.blue}>
            {productGroup?.name
              ? productGroup?.name?.[currentLang?.value]
              : '-'}
          </Text>
        )
      },
      {
        key: 'productForm',
        title: t('stock_administration.entry_form'),
        render: ({ productForm }) => (
          <Text weight={400} color={palette.green7}>
            {productForm?.name}
          </Text>
        )
      },
      {
        key: 'production_date',
        align: 'center',
        title: t('common.production_date'),
        render: ({ productionDate }) =>
          dayjs(productionDate).format(dayMonthYearBackSlash)
      },
      {
        title: t('common.expiration_date'),
        align: 'center',
        render: ({ expirationDate }) =>
          dayjs(expirationDate).format(dayMonthYearBackSlash)
      },
      {
        key: 'inDate',
        align: 'center',
        title: t('stock_administration.entry_date'),
        render: ({ inDate }) => dayjs(inDate).format(dayMonthYearBackSlash)
      },
      {
        key: 'inQuantity',
        title: t('common.quantity'),
        render: ({ currentQuantity }) => (
          <Popover
            placement="right"
            color={palette.colorBgSpotlight}
            styles={{ body: { padding: 0, borderRadius: 12 } }}
            content={<QuantityPopover quantity={currentQuantity} />}
          >
            <span className="pointer">
              {currentQuantity}{' '}
              <InfoCircleFilled
                style={{
                  color: palette.colorIcon
                }}
              />
            </span>
          </Popover>
        )
      },
      {
        key: 'tvaRate',
        title: t('common.tva'),
        align: 'center',
        render: ({ tvaRate }) => (
          <Text weight={400}>
            {tvaRate?.name ? tvaRate?.name?.[currentLang?.value] : '-'}
          </Text>
        )
      },
      {
        key: 'costPerUnitWithTva',
        title: t('common.cost_per_unit_with_tva'),
        align: 'center',
        render: ({ unitPrice, tvaRate }) => (
          <Text weight={400}>
            {getCostPerUnitTva(unitPrice, tvaRate?.name?.[currentLang?.value])}
          </Text>
        )
      },
      {
        key: 'total_tva',
        title: t('common.total_tva'),
        align: 'center',
        render: ({ currentQuantity, unitPrice, tvaRate }) => (
          <Text weight={400}>
            {getTotalTvaPrice(
              currentQuantity,
              unitPrice,
              tvaRate?.name?.[currentLang?.value]
            )}
          </Text>
        )
      },
      {
        key: 'info',
        fixed: 'right',
        title: t('common.info'),
        render: ({ currentQuantity, expirationDate }) => {
          const isExpired = dayjs().isAfter(
            dayjs(expirationDate, dayMonthYearBackSlash)
          )

          if (isExpired || !currentQuantity)
            return (
              <Tooltip
                title={isExpired ? t('stock.expired') : t('stock.out_of_stock')}
                placement="left"
              >
                {/* children need to be native html tags */}
                <div>{isExpired ? <DangerIcon /> : <WarningIcon />}</div>
              </Tooltip>
            )
        }
      }
    ],
    [t]
  )

  return { columns }
}
